<script setup lang="ts">
import type { Inactivity, CallDurationLimitsUI } from 'shared/src'
import { useValidations } from '@/composables/api/useValidations'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags.ts'
const { t } = useI18n()
const { showDevFeatures } = useFeatureFlags()

const inactivity = defineModel<Inactivity>('inactivity', {
  required: true,
})

const callDurationLimits = defineModel<CallDurationLimitsUI>('callDurationLimits', {
  required: true,
})

const { visibleErrors } = useValidations()
</script>

<template>
  <div class="flex flex-col space-y-6">
    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('inactivity.section_title')"
      :subtitle="t('inactivity.section_subtitle')"
    >
      <div class="col-span-2 flex flex-col space-y-6">
        <OInput
          v-model="inactivity.user_away_timeout"
          :min="5"
          :max="15"
          required
          :label="t('inactivity.user_away_timeout.label')"
          :tooltip-content="t('inactivity.user_away_timeout.description')"
          type="number"
          size="M"
          :widthSize="2"
          data-error-for="user_away_timeout"
          :error-message="visibleErrors.conversational_flow?.user_away_timeout"
          dataTestid="user-away-timeout"
        />

        <OInput
          v-model="inactivity.max_retries_user_away"
          :min="1"
          :max="10"
          required
          :label="t('inactivity.max_retries_user_away.label')"
          :tooltip-content="t('inactivity.max_retries_user_away.description')"
          type="number"
          :widthSize="2"
          size="M"
          data-error-for="max_retries_user_away"
          :error-message="visibleErrors.conversational_flow?.max_retries_user_away"
          dataTestid="max-retries-user-away"
        />

        <VariableMentionTextArea
          v-model="inactivity.re_engagement_description"
          :label="t('inactivity.re_engagement_description.label')"
          :tooltip-content="t('inactivity.re_engagement_description.description')"
          :placeholder="t('inactivity.re_engagement_description.placeholder')"
          dataTestid="reengagement-description"
        />
        <OInput
          v-model="inactivity.inactivity_output"
          required
          :label="t('inactivity.inactivity_output.label')"
          :tooltip-content="t('inactivity.inactivity_output.description')"
          data-error-for="inactivity_output"
          :error-message="visibleErrors.conversational_flow?.inactivity_output"
          dataTestid="inactivity-output"
        />
      </div>
    </OCardAdvanced>

    <OCardAdvanced
      elevation="S"
      border-radius="XL"
      content-class="border border-neutral-5"
      inline
      :title="t('duration_limit')"
      :subtitle="t('duration.limit.subtitle')"
    >
      <div class="col-span-2 flex flex-col space-y-6">
        <OInput
          v-model="callDurationLimits.max_call_duration_seconds"
          :min="1"
          required
          :label="t('max_call_duration.label')"
          :subtitle="t('max_call_duration.subtitle')"
          type="number"
          :widthSize="2"
          size="M"
          dataTestid="max-call-duration"
        />

        <div class="max-call-reached-message-wrapper -mx-1">
          <VariableMentionMultiTextArea
            v-model="callDurationLimits.max_call_duration_speech"
            :fields="['message']"
            :placeholders="[t('max_call_duration_reached_message.placeholder')]"
            :label="t('max_call_duration_reached_message.label')"
            :tooltip-content="t('max_call_duration_reached_message.subtitle')"
            dataTestid="max-call-reached-message"
          />
        </div>

        <OInput
          v-model="callDurationLimits.max_call_duration_output"
          :placeholder="t('max_call_duration_output.placeholder')"
          :label="t('max_call_duration_output.label')"
          :tooltip-content="t('max_call_duration_output.subtitle')"
          data-error-for="max_call_duration_output"
          required
          :error-message="visibleErrors.call_duration_limits?.max_call_duration_output"
          dataTestid="max-call-duration-output"
        />

        <OInput
          v-if="showDevFeatures"
          v-model="callDurationLimits.max_concurrent_calls"
          :min="1"
          required
          :label="t('max_concurrent_calls.label')"
          :subtitle="t('max_concurrent_calls.subtitle')"
          type="number"
          :widthSize="2"
          size="M"
          :max="25"
          dataTestid="max-concurent-calls"
        />
      </div>
    </OCardAdvanced>
  </div>
</template>

<style scoped>
.max-call-reached-message-wrapper :deep(.ml-1\.5) {
  margin-left: calc(var(--spacing) * 1);
}
</style>
